import React, { useState, useEffect } from "react";
import { Plus, Minus } from "lucide-react";

const faqs = [
  {
    q: "Wat kost het IT Support-abonnement?",
    a: "Voor particulieren is dit €15 per maand, of €150 per jaar bij vooruitbetaling (2 maanden gratis). Voor zakelijke klanten is dit €25 per maand, of €250 per jaar.",
  },
  {
    q: "Wat zit er precies in het abonnement?",
    a: "U krijgt professionele Bitdefender-beveiliging, twee onderhoudsbeurten per jaar, onbeperkte hulp op afstand en een vast aanspreekpunt. Bij problemen krijgt u bovendien voorrang op reguliere klanten.",
  },
  {
    q: "Kan ik het abonnement op elk moment opzeggen?",
    a: "Het maandabonnement is maandelijks opzegbaar. Het jaarabonnement loopt telkens één jaar en wordt stilzwijgend verlengd tenzij u uiterlijk één maand voor de einddatum opzegt.",
  },
  {
    q: "Geldt het abonnement voor meerdere apparaten?",
    a: "Het particuliere abonnement geldt voor één apparaat. Voor extra apparaten binnen hetzelfde huishouden of bedrijf bieden wij een aantrekkelijk gecombineerd tarief — neem gerust contact op voor een offerte op maat.",
  },
  {
    q: "Hoe snel kunnen jullie mijn computer repareren?",
    a: "De meeste reparaties worden binnen 24 uur uitgevoerd, vaak zelfs dezelfde dag. Bij complexere problemen of als onderdelen besteld moeten worden, informeren wij u vooraf over de verwachte doorlooptijd.",
  },
  {
    q: "Wat kost een reparatie gemiddeld?",
    a: "Onze tarieven beginnen vanaf € 45 voor kleinere reparaties. U ontvangt altijd vooraf een duidelijke prijsopgave, zodat u niet voor verrassingen komt te staan. Diagnose is gratis en vrijblijvend.",
  },
  {
    q: "Komen jullie ook aan huis?",
    a: "Ja, wij komen overal in Nederland aan huis of op kantoor. In de regio IJmond (Beverwijk, Heemskerk, Velsen, IJmuiden, Santpoort, Wijk aan Zee, Uitgeest, Castricum) brengen wij geen voorrijkosten in rekening. Daarbuiten geldt een kleine kilometervergoeding — vraag gerust naar de mogelijkheden.",
  },
  {
    q: "Welke merken en apparaten repareren jullie?",
    a: "Wij repareren alle bekende merken, waaronder Apple (MacBook, iMac), HP, Dell, Lenovo, ASUS, Acer, MSI en meer. Zowel laptops, desktops, all-in-ones als randapparatuur.",
  },
  {
    q: "Krijg ik garantie op de reparatie?",
    a: "Ja, op elke reparatie geven wij 3 maanden garantie. Mocht hetzelfde probleem binnen die periode terugkeren, dan lossen wij het kosteloos op.",
  },
  {
    q: "Is mijn data veilig bij jullie?",
    a: "Absoluut. Uw privacy en gegevens worden met de grootste zorg behandeld. Wij werken volgens de AVG-richtlijnen en maken waar nodig een backup voordat we aan de reparatie beginnen.",
  },
  {
    q: "Hoe werkt hulp op afstand?",
    a: "Via een veilige verbinding met Splashtop SOS kijken wij op uw scherm mee. U ontvangt een downloadlink en deelt een unieke 9-cijferige code met ons — na afloop wordt de verbinding volledig verbroken.",
  },
  {
    q: "Kan ik betalen met pin of factuur?",
    a: "Ja, u kunt betalen met pin, contant of per factuur (iDEAL/overboeking). Voor zakelijke klanten bieden wij ook periodieke facturatie aan.",
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);

  // Inject FAQPage JSON-LD schema for rich results
  useEffect(() => {
    const scriptId = "faq-jsonld";
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = scriptId;
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    });
    document.head.appendChild(script);
    return () => {
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, []);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-[1280px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-sm tracking-[0.3em] uppercase text-neutral-500 mb-3">
              — Veelgestelde vragen
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-5 leading-tight">
              Vragen?
              <br />
              <span className="italic font-light">Wij beantwoorden ze graag.</span>
            </h2>
            <p className="text-lg text-neutral-600 leading-relaxed mb-6">
              Staat uw vraag er niet tussen? Neem gerust contact met ons op — wij helpen u
              persoonlijk verder.
            </p>
            <a
              href="mailto:info@computheer.nl"
              className="inline-flex items-center gap-2 text-neutral-900 font-medium border-b border-neutral-900 pb-1 hover:gap-3 transition-all"
            >
              Stuur ons een e-mail →
            </a>
          </div>

          <div className="lg:col-span-7">
            <div className="border-t border-neutral-200">
              {faqs.map((faq, idx) => (
                <div key={idx} className="border-b border-neutral-200">
                  <button
                    onClick={() => setOpen(open === idx ? -1 : idx)}
                    className="w-full py-5 flex items-center justify-between gap-4 text-left group"
                    aria-expanded={open === idx}
                  >
                    <span className="text-base md:text-lg font-semibold text-neutral-900 group-hover:text-neutral-700 transition-colors">
                      {faq.q}
                    </span>
                    <span className="flex-shrink-0 w-8 h-8 rounded-full border border-neutral-300 flex items-center justify-center text-neutral-700 group-hover:border-neutral-900 group-hover:bg-neutral-900 group-hover:text-white transition-colors">
                      {open === idx ? <Minus size={15} /> : <Plus size={15} />}
                    </span>
                  </button>
                  <div
                    className={`grid transition-all duration-300 ease-out ${
                      open === idx ? "grid-rows-[1fr] opacity-100 pb-6" : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="text-neutral-600 leading-relaxed pr-10">{faq.a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
